import { makeAutoObservable, runInAction } from 'mobx';
import { Socket } from 'socket.io-client';
import { Interview, InterviewResult, InterviewStore } from '../types/interview';
import { interviewApi, userApi } from '../api/';
import { PersonalDataStore } from './personalDataStore';
import { personalDataStore } from './personalDataStore';
import { omit } from 'lodash';
import {
  InterviewScoresFailedPayload,
  InterviewScoresReadyPayload,
  JOB_TYPES,
  wsJobFailedEvent,
  wsJobReadyEvent,
} from '../types/job';

/**
 * Включить асинхронный поток оценок (через auth-прокси + BullMQ + WebSocket).
 * По умолчанию старый прямой запрос в core. Включается env-флагом.
 */
const USE_ASYNC_SCORES = process.env.REACT_APP_USE_ASYNC_SCORES === 'true';

/** localStorage-ключ для recovery после F5/разрыва сети. */
const pendingJobKey = (interviewId: string) => `interview.scores.pending.${interviewId}`;

class InterviewStoreImpl implements InterviewStore {
  currentInterview: Interview | null = null;
  loading = false;
  error: string | null = null;
  questions: string[] | null = null;
  answers: string[] | null = null;
  // scores: InterviewResult | null = null;
  personalDataStore: PersonalDataStore | null = null;
  currentInterviewToResult: Interview | null = null 
  currentChunkIndex = 0;
  constructor() {
    makeAutoObservable(this);
  }

  attachPersonalDataStore(store: PersonalDataStore) {
    this.personalDataStore = store;
  }

  /**
   * Начать новое интервью
   */
  async startInterview(id: string, vacancyId?: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      if (this.personalDataStore) {
        await this.personalDataStore.getInterviewList();
        const existing = this.personalDataStore.interviewList.filter(i => !i.finish).find(
          (interview) => interview.extractInterviewId === id || interview.interviewId === id
        );
        if (existing) {
          runInAction(() => {
            this.currentInterview = {
              ...existing,
              id: (existing.interviewId ?? existing.id) as string
            };
            this.currentChunkIndex = 0;
          });
          return;
        }
      }

      const interview = await interviewApi.startInterview(id);
      await personalDataStore.addinterviewtoUser({
        ...interview,
        extractInterviewId: id,
        position: personalDataStore.data.position,
        speciality: personalDataStore.data.speciality,
        ...(vacancyId ? { vacancyId } : {}),
      });
      // await personalDataStore.updateUser({ currentInterviewId: interview?.id });

      runInAction(() => {
        this.currentInterview = interview;
        this.currentChunkIndex = 0;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Получить интервью по ID
   */
  async getInterview(id: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const interview = await interviewApi.getInterviewById(id);

      runInAction(() => {
        this.currentInterview = interview;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async sendVideoChunk(videoChunk: File | Blob, options?: { timeout?: number }) {
    try {
      await interviewApi.sendVideoChunk(
        this.currentInterview?.id as string,
        videoChunk,
        this.currentChunkIndex,
        options
      );
      runInAction(() => {
        this.currentChunkIndex++;
      });
    } catch (error) {
      const code = (error as any)?.code;
      const message = error instanceof Error ? error.message : '';
      const isTimeout =
        code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(message);

      if (isTimeout) {
        // Таймаут считаем «доставкой без подтверждения»: двигаем индекс, чтобы следующий чанк
        // не перезатёр текущий, и не падаем наверх — пусть запись продолжается.
        runInAction(() => {
          this.currentChunkIndex++;
        });
        return;
      }

      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
      });
      // Важно: пробрасываем ошибку наверх, чтобы очередь чанков и UI могли отреагировать.
      // Без этого currentChunkIndex не инкрементируется, но следующий чанк пойдёт под тем же индексом → потеря данных.
      throw error;
    }
  }

  /**
   * Обновить интервью
   */
  async updateInterview(id: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const updatedInterview = await interviewApi.getNextQuestion(id);
      await personalDataStore.updateUserInterview(this.currentInterview?.id as string, {
        ...updatedInterview,
        position: personalDataStore.data.position,
        speciality: personalDataStore.data.speciality
      });

      runInAction(() => {
        this.currentInterview = updatedInterview;
        this.currentChunkIndex = 0;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async saveScores(scores: InterviewResult, id: string) {
    try {
      // if(this.currentInterview?.report) return
      const interview = await userApi.setResults(id, {
        interview_id: id,
        ...omit(scores, 'interview_id')
      });
      runInAction(() => {
        this.currentInterview = interview
        this.currentInterviewToResult = interview
      });
    } catch(e) {
      runInAction(() => {
        this.error = 'Произошла ошибка при попытке получить результаты';
      });
    }
  }

  /**
   * Получить результаты интервью.
   *
   * Два режима (флаг REACT_APP_USE_ASYNC_SCORES):
   *  - false (по умолчанию): старый прямой запрос в core + setResults на auth.
   *  - true: ставит job на auth → 202 { jobId } → подписка на WS-событие
   *    `interview.scores.ready` (через socket из SocketContext).
   *
   * В асинхронном режиме метод возвращает управление сразу после постановки
   * джобы (без await на её завершение). Финализация делается в `applyScoresResult`,
   * который вызывается WS-handler'ом или recovery-эндпоинтом.
   *
   * @param socket — нужен только в async-режиме. Передаётся из page через SocketContext.
   *                 Если null — async-режим деградирует до синхронного fallback.
   */
  async getInterviewScores(
    id: string,
    selfScore: Record<string, number>,
    socket?: Socket | null,
  ): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      // Идемпотентность: если результат уже есть в текущем интервью — отдаём кэш.
      if (this.currentInterview?.result && typeof this.currentInterview.result !== 'string') {
        runInAction(() => {
          this.currentInterviewToResult = this.currentInterview;
          this.loading = false;
        });
        return;
      }
      await this.personalDataStore?.getInterviewList();
      const currentInterview = this.personalDataStore?.interviewList.find(
        (interview) => interview.interviewId === id
      );
      if (currentInterview?.result?.final_result) {
        runInAction(() => {
          this.currentInterview = currentInterview;
          this.currentInterviewToResult = currentInterview;
          this.loading = false;
        });
        return;
      }

      if (USE_ASYNC_SCORES && socket) {
        // НОВЫЙ ПОТОК: ставим джобу, WS-handler финализирует.
        const { jobId } = await userApi.startScoresJob(id, selfScore);
        localStorage.setItem(pendingJobKey(id), jobId);
        this.subscribeToScoresJob(socket, jobId, id);
        // loading сбросит applyScoresResult / applyScoresError
        return;
      }

      // СТАРЫЙ ПОТОК: фронт-посредник между core и auth.
      const scores = await interviewApi.getInterviewScores(id, selfScore);
      await this.saveScores(scores, id);
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
        this.loading = false;
      });
    }
  }

  /**
   * Recovery после F5/разрыва WS. Вызывается со страницы результатов,
   * если в localStorage есть незавершённый jobId.
   *
   * Логика:
   *   - DONE → берём result, чистим localStorage.
   *   - FAILED → выставляем error, чистим localStorage.
   *   - PENDING/PROCESSING → подписываемся на WS и ждём.
   *
   * Возвращает true, если возобновление сработало (поток продолжается) —
   * в этом случае page НЕ должен запускать обычный getInterviewScores.
   */
  async resumePendingScoresJob(interviewId: string, socket?: Socket | null): Promise<boolean> {
    const jobId = localStorage.getItem(pendingJobKey(interviewId));
    if (!jobId) return false;

    try {
      const job = await userApi.getJob(jobId);

      if (job.status === 'DONE') {
        this.applyScoresResult({
          jobId,
          interviewId,
          result: job.result,
        });
        return true;
      }
      if (job.status === 'FAILED') {
        this.applyScoresError({
          jobId,
          interviewId,
          error: job.error || 'Не удалось рассчитать оценки',
        });
        return true;
      }
      // Ещё в работе — подписываемся и ждём.
      if (socket) {
        runInAction(() => {
          this.loading = true;
          this.error = null;
        });
        this.subscribeToScoresJob(socket, jobId, interviewId);
        return true;
      }
      // Сокета нет — даём странице упасть в обычный flow,
      // не трогаем pending-ключ (попробуем ещё раз позже).
      return false;
    } catch {
      // Если auth недоступен / 404 на job — чистим pending и идём по обычному пути.
      localStorage.removeItem(pendingJobKey(interviewId));
      return false;
    }
  }

  /**
   * Подписка на WS-события конкретной джобы. Снимает свои listener'ы
   * после первого подходящего события или при ошибке.
   */
  private subscribeToScoresJob(socket: Socket, jobId: string, interviewId: string) {
    const readyEvent = wsJobReadyEvent(JOB_TYPES.INTERVIEW_SCORES);
    const failedEvent = wsJobFailedEvent(JOB_TYPES.INTERVIEW_SCORES);

    const onReady = (payload: InterviewScoresReadyPayload) => {
      if (payload.jobId !== jobId) return;
      cleanup();
      this.applyScoresResult(payload);
    };
    const onFailed = (payload: InterviewScoresFailedPayload) => {
      if (payload.jobId !== jobId) return;
      cleanup();
      this.applyScoresError(payload);
    };
    const cleanup = () => {
      socket.off(readyEvent, onReady);
      socket.off(failedEvent, onFailed);
    };

    socket.on(readyEvent, onReady);
    socket.on(failedEvent, onFailed);
  }

  private applyScoresResult(payload: InterviewScoresReadyPayload) {
    const interview = payload.result as Interview;
    localStorage.removeItem(pendingJobKey(payload.interviewId));
    runInAction(() => {
      this.currentInterview = interview;
      this.currentInterviewToResult = interview;
      this.loading = false;
      this.error = null;
    });
  }

  private applyScoresError(payload: InterviewScoresFailedPayload) {
    localStorage.removeItem(pendingJobKey(payload.interviewId));
    runInAction(() => {
      this.error = payload.error || 'Произошла ошибка при попытке получить результаты';
      this.loading = false;
    });
  }

  /**
   * Сбросить состояние стора
   */
  resetState() {
    this.currentInterview = null;
    this.currentChunkIndex = 0;
    this.loading = false;
    this.error = null;
    this.questions = null;
    this.answers = null;
    this.currentInterviewToResult = null;
  }
}

export const interviewStore = new InterviewStoreImpl();
